import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Skeleton,
  Box,
  Button
} from '@mui/material';
import { getProducts } from '../firebase/products';
import { getCategories } from '../firebase/categories';
import { Product, Category } from '../types';

const HomeProductSection = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [productsByCategory, setProductsByCategory] = useState<Record<string, Product[]>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [allProducts, allCategories] = await Promise.all([
        getProducts(),
        getCategories()
      ]);

      // Filter main categories (no parentId), sort with Hot first, and limit to first 5
      const mainCategories = allCategories
        .filter(c => !c.parentId)
        .sort((a, b) => {
          if (a.name === 'Hot') return -1;
          if (b.name === 'Hot') return 1;
          return (a.order || 999) - (b.order || 999);
        })
        .slice(0, 5);

      const productsMap: Record<string, Product[]> = {};

      mainCategories.forEach(category => {
        // Get products for this category
        const categoryProducts = allProducts
          .filter(p => p.categoryId === category.id)
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 4); // Limit to 4 products per category

        if (categoryProducts.length > 0) {
          productsMap[category.id] = categoryProducts;
        }
      });

      setCategories(mainCategories.filter(c => productsMap[c.id]?.length > 0));
      setProductsByCategory(productsMap);
    } catch (error) {
      console.error('Error loading home products:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-12 py-8">
        {[1, 2].map((i) => (
          <div key={i} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Skeleton width={200} height={40} sx={{ bgcolor: 'rgba(0, 255, 255, 0.1)', mb: 4 }} />
            <Grid container spacing={3}>
              {[1, 2, 3, 4].map((j) => (
                <Grid item xs={12} sm={6} md={3} key={j}>
                  <Skeleton variant="rectangular" height={300} sx={{ bgcolor: 'rgba(0, 255, 255, 0.1)' }} />
                </Grid>
              ))}
            </Grid>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-16 py-12">
      {categories.map((category) => (
        <div key={category.id} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center mb-8">
            <Typography 
              variant="h3" 
              component="h2"
              className="font-orbitron text-center mb-2"
              sx={{ 
                color: '#ffffff',
                textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
                fontFamily: 'Orbitron, sans-serif',
                fontWeight: 700,
                letterSpacing: '0.1em'
              }}
            >
              {category.name}
            </Typography>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-neon-blue to-transparent" />
          </div>

          <Grid container spacing={3}>
            {productsByCategory[category.id]?.map((product) => {
              const primaryImage = product.images.find(img => img.isPrimary);
              
              return (
                <Grid item xs={12} sm={6} md={3} key={product.id}>
                  <Link to={`/producto/${product.id}`} style={{ textDecoration: 'none' }}>
                    <Card sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      bgcolor: 'black',
                      border: '1px solid rgba(0, 255, 255, 0.1)',
                      boxShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: '0 0 20px rgba(0, 255, 255, 0.2)',
                        border: '1px solid rgba(0, 255, 255, 0.3)',
                      },
                    }}>
                      <Box sx={{ 
                        position: 'relative',
                        paddingTop: '100%', // 1:1 Aspect Ratio
                        overflow: 'hidden',
                        bgcolor: '#050505',
                      }}>
                        {primaryImage && (
                          <CardMedia
                            component="img"
                            image={primaryImage.url}
                            alt={product.name}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              p: 2,
                              transition: 'transform 0.5s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                              },
                            }}
                          />
                        )}
                        {product.discountPercentage && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 10,
                              right: 10,
                              bgcolor: 'rgba(0, 0, 0, 0.8)',
                              color: '#00ffff',
                              border: '1px solid #00ffff',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: 1,
                              fontWeight: 'bold',
                              fontFamily: 'Orbitron, sans-serif',
                              fontSize: '0.8rem',
                              backdropFilter: 'blur(4px)',
                            }}
                          >
                            -{product.discountPercentage}%
                          </Box>
                        )}
                      </Box>
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        bgcolor: 'rgba(0, 0, 0, 0.8)',
                        borderTop: '1px solid rgba(255, 255, 255, 0.05)',
                        p: 2,
                      }}>
                        <Typography
                          variant="h6"
                          component="h3"
                          className="font-orbitron"
                          noWrap
                          sx={{
                            color: '#ffffff',
                            fontFamily: 'Orbitron, sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            mb: 1,
                            textShadow: '0 0 5px rgba(0, 255, 255, 0.3)',
                          }}
                        >
                          {product.name}
                        </Typography>
                        
                        <div className="flex justify-between items-center mt-2">
                          <Typography
                            variant="body1"
                            className="font-rajdhani"
                            sx={{
                              color: '#00ffff',
                              fontFamily: 'Rajdhani, sans-serif',
                              fontWeight: 700,
                              fontSize: '1.2rem',
                            }}
                          >
                            {product.price}€
                          </Typography>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </Grid>
              );
            })}
          </Grid>

          <div className="flex justify-center mt-8">
            <Button
              component={Link}
              to={`/categoria/${category.name.toLowerCase()}`}
              variant="outlined"
              className="font-orbitron"
              sx={{
                color: '#00ffff',
                borderColor: '#00ffff',
                fontFamily: 'Orbitron, sans-serif',
                fontWeight: 600,
                px: 4,
                py: 1,
                borderRadius: 2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#ffffff',
                  color: '#ffffff',
                  backgroundColor: 'rgba(0, 255, 255, 0.1)',
                  boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)',
                },
              }}
            >
              VER MÁS {category.name.toUpperCase()}
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default HomeProductSection;
